import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Contact } from "@/types/contacts";
import { checkSingleContactDuplicate } from "@/utils/duplicateChecker";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { AddEntityDialog } from "./AddEntityDialog";
import { AddEntityTypeDialog } from "./AddEntityTypeDialog";
import type { EntityType, BaseEntity } from "@/types/entities";
import { useUserRole } from "@/hooks/useUserRole";
import { useEntityTypes } from "@/hooks/useEntityTypes";

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (contact: Omit<Contact, 'id'>) => Promise<void>;
}

interface Entity {
  id: number;
  name: string;
  type: string;
  region: string;
  state: string;
}

export function AddContactDialog({
  open,
  onOpenChange,
  onAdd,
}: AddContactDialogProps) {
  const { toast } = useToast();
  const { isAdmin, isEditor } = useUserRole();
  const { entityTypes, refetch: refetchTypes } = useEntityTypes();
  const canCreateEntity = isAdmin || isEditor;
  
  const [loading, setLoading] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showNewEntityDialog, setShowNewEntityDialog] = useState(false);
  const [showNewTypeDialog, setShowNewTypeDialog] = useState(false);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState<string>("restaurant");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    position: "",
    cellPhone: "",
    phone: "",
    email: "",
    website: "",
  });

  // Buscar entidades ao abrir o dialog
  useEffect(() => {
    if (open) {
      fetchEntities();
    }
  }, [open]);

  const fetchEntities = async () => {
    try {
      const { data, error } = await supabase
        .from("entities")
        .select("id, name, type, region, state")
        .order("name");

      if (error) throw error;
      setEntities(data || []);
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  };

  const selectedEntity = entities.find(e => e.id === selectedEntityId);

  const filteredEntities = entities.filter(entity => {
    if (!searchQuery) return true;
    return entity.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      position: "",
      cellPhone: "",
      phone: "",
      email: "",
      website: "",
    });
    setSelectedEntityId(null);
    setSearchQuery("");
  };

  const handleSubmit = async (forceAdd: boolean = false) => {
    // Validação básica
    if (!selectedEntityId || !formData.firstName.trim() || !formData.email.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione uma empresa e preencha Nome e E-mail.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const entity = entities.find(e => e.id === selectedEntityId);
      if (!entity) {
        toast({
          title: "Erro",
          description: "Entidade não encontrada.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Verifica duplicação
      if (!forceAdd) {
        const isDuplicate = await checkSingleContactDuplicate(
          formData.email,
          entity.name
        );

        if (isDuplicate) {
          setLoading(false);
          setShowDuplicateAlert(true);
          return;
        }
      }

      // Monta o contato completo com dados da entidade
      const contactData: Omit<Contact, 'id'> = {
        ...formData,
        company: entity.name,
        entityType: entity.type as "restaurant" | "operator" | "consolidator",
        region: entity.region || "",
        state: entity.state || "",
        city: "",
      };

      // Adiciona o contato
      await onAdd(contactData);

      toast({
        title: "Contato adicionado",
        description: `Contato vinculado à entidade "${entity.name}".`,
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error adding contact:", error);
      toast({
        title: "Erro ao adicionar",
        description: "Não foi possível criar o contato.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExisting = async () => {
    setShowDuplicateAlert(false);
    await handleSubmit(true);
  };

  const handleAddEntity = async (entityData: Omit<BaseEntity, 'id'>) => {
    try {
      const entityToAdd = {
        ...entityData,
        type: selectedEntityType, // Usar tipo selecionado
      };

      const { data, error } = await supabase
        .from("entities")
        .insert([entityToAdd])
        .select()
        .single();

      if (error) throw error;

      // Recarrega entidades e seleciona a nova
      await fetchEntities();
      setSelectedEntityId(data.id);
      setSearchQuery(data.name);
      
      toast({
        title: "Empresa criada",
        description: "Nova empresa criada e selecionada com sucesso.",
      });
    } catch (error: any) {
      console.error("Error adding entity:", error);
      
      let errorMessage = "Não foi possível criar a empresa.";
      
      if (error.code === '42501') {
        errorMessage = "Você não tem permissão para criar empresas.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao criar empresa",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Contato</DialogTitle>
            <DialogDescription>
              Preencha os dados do contato. Campos com * são obrigatórios.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="entityType">Tipo de Empresa *</Label>
              <Select
                value={selectedEntityType}
                onValueChange={(value) => {
                  if (value === "new") {
                    setShowNewTypeDialog(true);
                  } else {
                    setSelectedEntityType(value);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {entityTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.label}
                    </SelectItem>
                  ))}
                  {canCreateEntity && (
                    <SelectItem value="new" className="text-primary font-medium">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        <span>Novo Tipo</span>
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="entity">Empresa *</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedEntityId?.toString()}
                  onValueChange={(value) => {
                    if (value === "new") {
                      setShowNewEntityDialog(true);
                    } else {
                      setSelectedEntityId(Number(value));
                    }
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione uma empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="p-2">
                      <Input
                        placeholder="Buscar empresa..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mb-2"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {canCreateEntity ? (
                      <SelectItem value="new" className="text-primary font-medium">
                        <div className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          <span>Nova Empresa</span>
                        </div>
                      </SelectItem>
                    ) : (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground border-b">
                        ⚠️ Apenas admin/editor podem criar empresas
                      </div>
                    )}
                    {filteredEntities.length > 0 ? (
                      filteredEntities.map((entity) => (
                        <SelectItem key={entity.id} value={entity.id.toString()}>
                          <div className="flex flex-col">
                            <span className="font-medium">{entity.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {entity.state} • {entity.type}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-muted-foreground">
                        Nenhuma empresa encontrada
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedEntity && (
                <div className="text-sm text-muted-foreground mt-1">
                  {selectedEntity.type === "restaurant" && "Restaurante"}
                  {selectedEntity.type === "operator" && "Operadora"}
                  {selectedEntity.type === "consolidator" && "Consolidadora"}
                  {" • "}
                  {selectedEntity.state}
                  {selectedEntity.region && ` • ${selectedEntity.region}`}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstName">Nome *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="Nome"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Sobrenome</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Sobrenome"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Cargo</Label>
              <Input
                id="position"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder="Cargo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cellPhone">Celular</Label>
              <Input
                id="cellPhone"
                value={formData.cellPhone}
                onChange={(e) => setFormData({ ...formData, cellPhone: e.target.value })}
                placeholder="(00) 00000-0000"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefone Fixo</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(00) 0000-0000"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="www.exemplo.com"
              />
            </div>

          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                onOpenChange(false);
              }}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar Contato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDuplicateAlert} onOpenChange={setShowDuplicateAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Contato já existe</AlertDialogTitle>
            <AlertDialogDescription>
              Já existe um contato com o e-mail "{formData.email}" na empresa "{selectedEntity?.name}".
              Deseja atualizar os dados existentes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleUpdateExisting}>
              Atualizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddEntityDialog
        open={showNewEntityDialog}
        onOpenChange={setShowNewEntityDialog}
        onAdd={handleAddEntity}
        entityType={selectedEntityType as EntityType}
        entityTitle="Empresa"
      />

      <AddEntityTypeDialog
        open={showNewTypeDialog}
        onOpenChange={setShowNewTypeDialog}
        onSuccess={refetchTypes}
      />
    </>
  );
}